import {
  CognitoIdentityProviderClient,
  InitiateAuthCommand,
  //RespondToAuthChallengeCommand,
  GetUserCommand,
  SignUpCommand,
  ConfirmSignUpCommand,
  ResendConfirmationCodeCommand,
  UpdateUserAttributesCommand,
  VerifyUserAttributeCommand,
  GetUserAttributeVerificationCodeCommand,
  ForgotPasswordCommand,
  ConfirmForgotPasswordCommand,
} from '@aws-sdk/client-cognito-identity-provider';

interface SignUpData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  //companyName?: string;
  phoneNumber?: string;
}

class CognitoAuthService {
  private client: CognitoIdentityProviderClient;
  private clientId: string;

  constructor() {
    this.client = new CognitoIdentityProviderClient({
      region: import.meta.env.VITE_COGNITO_REGION || 'us-east-1',
    });
    this.clientId = import.meta.env.VITE_COGNITO_CLIENT_ID;
  }

  async signUp(data: SignUpData) {
    try {
      const command = new SignUpCommand({
        ClientId: this.clientId,
        Username: data.email,
        Password: data.password,
        UserAttributes: [
          {
            Name: 'email',
            Value: data.email,
          },
          {
            Name: 'given_name',
            Value: data.firstName,
          },
          {
            Name: 'family_name',
            Value: data.lastName,
          },
/*           {
            Name: 'phone_number',
            Value: data.phoneNumber.startsWith('+') ? data.phoneNumber : `+1${data.phoneNumber.replace(/\D/g, '')}`,
          }, */
/*           ...(data.companyName ? [{
            Name: 'custom:company_name',
            Value: data.companyName,
          }] : []), */
        ],
      });

      const response = await this.client.send(command);
      return response;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async confirmSignUp(email: string, code: string) {
    try {
      const command = new ConfirmSignUpCommand({
        ClientId: this.clientId,
        Username: email,
        ConfirmationCode: code,
      });

      const response = await this.client.send(command);
      return response;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async resendConfirmationCode(email: string) {
    try {
      const command = new ResendConfirmationCodeCommand({
        ClientId: this.clientId,
        Username: email,
      });

      const response = await this.client.send(command);
      return response;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async signIn(email: string, password: string) {
    try {
      const command = new InitiateAuthCommand({
        AuthFlow: 'USER_PASSWORD_AUTH',
        ClientId: this.clientId,
        AuthParameters: {
          USERNAME: email,
          PASSWORD: password,
        },
      });

      const response = await this.client.send(command);
      
      if (response.AuthenticationResult) {
        return {
          accessToken: response.AuthenticationResult.AccessToken,
          idToken: response.AuthenticationResult.IdToken,
          refreshToken: response.AuthenticationResult.RefreshToken,
        };
      }

      // Handle MFA or other challenges
      if (response.ChallengeName) {
        throw new Error(`Challenge required: ${response.ChallengeName}`);
      }

      throw new Error('Authentication failed');
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // NEW: OAuth token exchange method for Google/Facebook login
async exchangeCodeForTokens(code: string) {
  try {
    const cognitoDomain = import.meta.env.VITE_COGNITO_DOMAIN;
    const redirectUri = window.location.origin;
    
    console.log('🔍 Exchange tokens - Domain:', cognitoDomain);
    console.log('🔍 Exchange tokens - Client ID:', this.clientId);
    console.log('🔍 Exchange tokens - Redirect URI:', redirectUri);
    console.log('🔍 Exchange tokens - Code:', code);
    
    if (!cognitoDomain) {
      throw new Error('VITE_COGNITO_DOMAIN environment variable is not set');
    }

    const tokenUrl = `${cognitoDomain}/oauth2/token`;
    console.log('🔍 Token URL:', tokenUrl);

    const response = await fetch(tokenUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        grant_type: 'authorization_code',
        client_id: this.clientId,
        code: code,
        redirect_uri: redirectUri,
      }),
    });
    
    console.log('🔍 Token response status:', response.status);
    
    if (!response.ok) {
      const errorData = await response.text();
      console.error('🔍 Token exchange error:', errorData);
      throw new Error(`Failed to exchange code for tokens: ${errorData}`);
    }
    
    const tokens = await response.json();
    console.log('🔍 Tokens received successfully:', tokens);
    
    return {
      accessToken: tokens.access_token,
      idToken: tokens.id_token,
      refreshToken: tokens.refresh_token,
    };
  } catch (error) {
    console.error('🔍 Exchange tokens error:', error);
    throw this.handleError(error);
  }
}

async updateUserAttributes(
  accessToken: string,
  attributes: Array<{Name: string, Value: string}>
): Promise<void> {
  try {
    const command = new UpdateUserAttributesCommand({
      AccessToken: accessToken,
      UserAttributes: attributes,
    });

    await this.client.send(command);
  } catch (error) {
    throw this.handleError(error);
  }
}

async verifyUserAttribute(
  accessToken: string,
  attributeName: string,
  confirmationCode: string
): Promise<void> {
  try {
    const command = new VerifyUserAttributeCommand({
      AccessToken: accessToken,
      AttributeName: attributeName,
      Code: confirmationCode,
    });

    await this.client.send(command);
  } catch (error) {
    throw this.handleError(error);
  }
}

async resendAttributeVerification(
  accessToken: string,
  attributeName: string
): Promise<void> {
  try {
    const command = new GetUserAttributeVerificationCodeCommand({
      AccessToken: accessToken,
      AttributeName: attributeName,
    });

    await this.client.send(command);
  } catch (error) {
    throw this.handleError(error);
  }
}

async getCurrentUser(accessToken: string): Promise<Record<string, unknown>> {
  try {
    const command = new GetUserCommand({
      AccessToken: accessToken,
    });

    const response = await this.client.send(command);

    // Convert user attributes to a more usable format
    const userAttributes: Record<string, string> = {};
    if (response.UserAttributes) {
      response.UserAttributes.forEach(attr => {
        if (attr.Name && attr.Value) {
          userAttributes[attr.Name] = attr.Value;
        }
      });
    }

    return {
      username: response.Username,
      attributes: userAttributes,
      // Removed userStatus since it doesn't exist on GetUserCommandOutput
    };
  } catch (error) {
    throw this.handleError(error);
  }
}



async forgotPassword(email: string): Promise<void> {
  try {
    const command = new ForgotPasswordCommand({
      ClientId: this.clientId,
      Username: email,
    });

    await this.client.send(command);
  } catch (error) {
    throw this.handleError(error);
  }
}

async confirmForgotPassword(email: string, code: string, newPassword: string): Promise<void> {
  try {
    const command = new ConfirmForgotPasswordCommand({
      ClientId: this.clientId,
      Username: email,
      ConfirmationCode: code,
      Password: newPassword,
    });

    await this.client.send(command);
  } catch (error) {
    throw this.handleError(error);
  }
}

  private handleError(error: unknown): Error {
    if (error && typeof error === 'object' && 'name' in error) {
      const awsError = error as { name: string; message?: string };
      return new Error(awsError.message || `AWS Error: ${awsError.name}`);
    }
    
    if (error instanceof Error) {
      return error;
    }
    
    return new Error('Admin operation failed');
  }
}

export const cognitoAuthService = new CognitoAuthService();
export type { SignUpData };